import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import type { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronDownSmall from "atoms/icon/icons/ic_chevron_down_small_ol.svg";
import ChevronUpSmall from "atoms/icon/icons/ic_chevron_up_small_ol.svg";
import RewardsIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import { InclusionDetails } from "molecules/inclusion/InclusionDetails";

interface FreePackageGroupDetailsProps {
  inclusions?: Models<"InclusionListingsModel">;
  title: string;
  customDisclaimer?: string;
  itemType: "reward" | "offer";
}

export const FreePackageGroupDetails = ({
  inclusions,
  title,
  customDisclaimer,
  itemType,
}: FreePackageGroupDetailsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showMore, setShowMore] = useState(true);
  const [showMoreButton, setShowMoreButton] = useState(false);
  useEffect(() => {
    if (ref.current) {
      const inclusionsListHeight = ref.current.offsetHeight;
      if (inclusionsListHeight >= 500) {
        setShowMore(false);
        setShowMoreButton(true);
      }
    }
  }, [ref]);

  const hasMembershipInclusions = !!inclusions?.memberships?.length;

  const renderShowMoreButton = () => (
    <button
      className="flex cursor-pointer pt-[6px]"
      onClick={() => setShowMore((x) => !x)}
    >
      <Icon
        svg={showMore ? ChevronUpSmall : ChevronDownSmall}
        size="small"
        color="brandColor"
      />
      <div className="ml-3 text-bold1 text-brand-color">
        {showMore ? "Show Less" : "Show More"}
      </div>
    </button>
  );

  const renderGradient = () => {
    return (
      <div className="absolute bottom-0 h-10 w-full bg-gradient-to-t from-background-two to-transparent" />
    );
  };

  const renderInclusions = (inclusions?: Models<"InclusionListingsModel">) => {
    if (!inclusions) return null;
    return (
      inclusions &&
      Object.entries(inclusions)
        .filter(
          ([key]) =>
            key === "treatments" ||
            key === "packages" ||
            key === "memberships" ||
            key === "products"
        )
        .map(([key, value]) => {
          if (!Array.isArray(value) || value.length === 0) return null;

          return (
            <div key={key}>
              <div className="py-2 pl-1 text-bold1 capitalize">{key}</div>
              <ul className="ml-6 list-disc">
                {value.map((item) => {
                  const typedItem = item as Models<"InclusionModel">;

                  return (
                    <InclusionDetails
                      key={typedItem.id}
                      typedItem={typedItem}
                      inclusionType={key}
                      isFreePackageGroup={true}
                    />
                  );
                })}
              </ul>
            </div>
          );
        })
    );
  };

  return (
    <>
      <div className="mb-4 rounded bg-two px-4 py-5" ref={ref}>
        <div className="mb-1 flex items-center gap-3">
          <div>
            <Icon svg={RewardsIcon} size="xsmall" color="brandColor" />
          </div>
          <div className="text-bold1 text-primary">{title}</div>
        </div>

        {
          <div
            className={cx(
              "relative flex flex-col overflow-hidden",
              !showMore && "max-h-[340px]"
            )}
          >
            {renderInclusions(inclusions)}
            {!showMore && renderGradient()}
          </div>
        }

        {showMoreButton && renderShowMoreButton()}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-center text-body2 italic text-secondary">
          This {itemType} can only be used once.
        </p>
        {hasMembershipInclusions && (
          <p className="text-center text-body2 italic text-secondary">
            Applies to the first month of membership only
          </p>
        )}
        {customDisclaimer && (
          <p className="text-center text-body2 italic text-secondary">
            {customDisclaimer}
          </p>
        )}
      </div>
    </>
  );
};
